import axios from "axios";

export const safeParse = (value) => (value && value !== "None" ? parseFloat(value) : 0);

export function calculateRevGrowthAvg(financialsChart, years, financialCurrency) {
  if (!financialsChart?.yearly?.length) return "0.00";
  const dataNew = financialsChart.yearly;
  const data = [...financialsChart.yearly].sort((a, b) => b.date - a.date);

  console.log("Previousdata:", data); // Debug

  if (years === 1) {
    const current = data[0].revenue;
    const previous = data[1].revenue;
    console.log("Previousdata:", current, "Previous:", previous); // Debug
    return (((current - previous) / previous) * 100).toFixed(2);
  }

  if (years === 3) {
    const data = [...financialsChart.yearly].sort((a, b) => b.date - a.date);
    console.log("Data:", data); // Debugging
    const growth2020to2021 = ((dataNew[1].revenue / dataNew[0].revenue) - 1) * 100;
    const growth2021to2022 = ((dataNew[2].revenue / dataNew[1].revenue) - 1) * 100;
    const growth2022to2023 = ((dataNew[3].revenue / dataNew[2].revenue) - 1) * 100;

    // Calculate the average growth
    const averageRevenueGrowth = ((growth2020to2021 + growth2021to2022 + growth2022to2023) / 3).toFixed(2);

    // Log results for debugging
    console.log("Year-over-Year Growth (2020 to 2021):", dataNew);
    console.log("Year-over-Year Growth (2021 to 2022):", growth2021to2022.toFixed(2));
    console.log("Year-over-Year Growth (2022 to 2023):", growth2022to2023.toFixed(2));
    console.log("Average Year-over-Year Revenue Growth:", averageRevenueGrowth);

    return averageRevenueGrowth;
  }

  return "0.00";
}

export function calculateAverageProfitMargin(yearlyData, years, financialCurrency) {
  if (!yearlyData?.length) return "0.00";
  console.log("Profit data:", yearlyData); // Debug

  if (years === 1) {
    const latest = [...yearlyData].sort((a, b) => b.date - a.date);
    return ((latest[0].earnings / latest[0].revenue) * 100).toFixed(2);
  }

  if (years === 3) {
    if (!yearlyData?.length) return "0.00";
    console.log("Profit data:", yearlyData); // Debug

    // Calculate profit margin for each year
    const profitMargins = yearlyData.map((year) => {
      if (year.revenue && year.revenue !== 0) {
        return (year.earnings / year.revenue) * 100;
      }
      return 0; // If there's no revenue, return 0
    });

    // Calculate the average profit margin
    const totalProfitMargin = profitMargins.reduce((sum, margin) => sum + margin, 0);
    console.log("year.revenue", totalProfitMargin);
    const averageProfitMargin = totalProfitMargin / profitMargins.length;

    // Round the result to two decimal places after the full calculation
    return averageProfitMargin.toFixed(2);
  }

  return "0.00";
}

export function calculateFcfMargin(cashFlowStatements, incomeStatements) {
  const operatingCashFlow = safeParse(cashFlowStatements[0].operatingCashflow);
  const capitalExpenditures = safeParse(cashFlowStatements[0].capitalExpenditures);
  const totalRevenue = safeParse(incomeStatements[0].totalRevenue);

  if (totalRevenue === 0) {
    console.error(
      `Invalid data for last year: operatingCashFlow = ${operatingCashFlow}, capitalExpenditures = ${capitalExpenditures}, totalRevenue = ${totalRevenue}`
    );
    return "0.00";
  }

  const fcf = operatingCashFlow - capitalExpenditures;
  const fcfMargin = (fcf / totalRevenue) * 100;
  return fcfMargin.toFixed(2);
}

export function calculateAverageFcfMargin(financialData, years) {
  try {
    if (!Array.isArray(financialData)) {
      console.error("Expected array of financial data");
      return "0.00";
    }

    // Sort data by date descending
    const sortedData = [...financialData].sort((a, b) => b.date - a.date);

    if (years === 1) {
      const latestYear = sortedData[0];
      if (!latestYear?.revenue || !latestYear?.earnings) {
        console.error("Missing revenue or earnings for latest year");
        return "0.00";
      }
      return ((latestYear.earnings / latestYear.revenue) * 100).toFixed(2);
    }

    if (years === 3) {
      let totalMargin = 0;
      let validYears = 0;

      for (let i = 0; i < Math.min(3, sortedData.length); i++) {
        const year = sortedData[i];
        if (year.revenue && year.earnings) {
          totalMargin += (year.earnings / year.revenue) * 100;
          validYears++;
        }
      }

      if (validYears === 0) {
        console.error("No valid years found for calculation");
        return "0.00";
      }

      return (totalMargin / validYears).toFixed(2);
    }

    return "0.00";
  } catch (error) {
    console.error("FCF calculation error:", error);
    return "0.00";
  }
}

// Remove any calls to "calculateROE" referencing undefined variables and
// remove references to "financialData" not defined in this function.
export function setInputValues(
  type,
  value,
  key,
  setRoe,
  setRevGrowth,
  setProfitMargin,
  setFcfMargin,
  setPeRatios,
  setpfcf
) {
  switch (type) {
    case "roe":
      setRoe((prev) => ({
        ...prev,
        inputs: { ...prev.inputs, [key]: value },
      }));
      break;

    case "revGrowth":
      setRevGrowth((prev) => ({
        ...prev,
        inputs: { ...prev.inputs, [key]: value },
      }));
      break;

    case "profitMargin":
      setProfitMargin((prev) => ({
        ...prev,
        inputs: { ...prev.inputs, [key]: value },
      }));
      break;

    case "fcfMargin":
      setFcfMargin((prev) => ({
        ...prev,
        inputs: { ...prev.inputs, [key]: value },
      }));
      break;

    case "pe":
      setPeRatios((prev) => ({
        ...prev,
        inputs: { ...prev.inputs, [key]: value },
      }));
      break;

    case "pfcf":
      setpfcf((prev) => ({
        ...prev,
        inputs: { ...prev.inputs, [key]: value },
      }));
      break;

    default:
      console.warn("Unknown metric type:", type);
  }
}

export function calculateFuturePrices({
  revGrowth,
  years,
  profitMargin,
  peRatios,
  currentRevenue,
  sharesOutstanding,
  currentStockPrice,
  setFuturePrices,
}) {
  if (sharesOutstanding === 0) {
    throw new Error("Shares Outstanding cannot be zero.");
  }
  if (!currentRevenue || !sharesOutstanding || !years) {
    console.log(currentRevenue, sharesOutstanding, years);
  }

  const calculateScenario = (growthRate, margin, peRatio) => {
    const growthRateDecimal = growthRate / 100;
    const marginDecimal = margin / 100;

    // For demonstration:
    const futureRevenue = currentRevenue * Math.pow(1 + growthRateDecimal, years);
    const futureNetIncome = futureRevenue * marginDecimal;
    const futureStockPrice = (futureNetIncome / sharesOutstanding) * peRatio;
    const currentPriceReturn =
      ((futureStockPrice - currentStockPrice) / currentStockPrice) * 100;

    return {
      futureRevenue: futureRevenue.toFixed(2),
      futureNetIncome: futureNetIncome.toFixed(2),
      futureStockPrice: futureStockPrice.toFixed(2),
      currentPriceReturn: `${currentPriceReturn.toFixed(2)}%`,
    };
  };

  const result = {
    low: calculateScenario(
      Number(revGrowth.inputs.low),
      Number(profitMargin.inputs.low),
      Number(peRatios.inputs.low)
    ),
    mid: calculateScenario(
      Number(revGrowth.inputs.mid),
      Number(profitMargin.inputs.mid),
      Number(peRatios.inputs.mid)
    ),
  };

  setFuturePrices(result);
}

export function clearInputValues(
  setFinancialData,
  setFuturePrices,
  setStockPrice,
  setCurrentRevenue,
  setPeRatios,
  setpfcf,
  setRoe,
  setRevGrowth,
  setProfitMargin,
  setFcfMargin,
  setDesiredAnualReturn
) {
  setFinancialData(null);
  setFuturePrices({
    low: null,
    mid: null,
  });
  setStockPrice(null);
  setCurrentRevenue(null);
  setPeRatios({
    peRatio1Year: 0,
    peRatio3Years: 0,
    inputs: {
      low: "",
      mid: "",
    },
  });
  setpfcf({
    pfcf1Year: 0,
    pfcf3Years: 0,
    inputs: {
      low: "",
      mid: "",
    },
  });
  setRoe({
    firstYearROE: null,
    inputs: {
      low: "",
    },
  });
  setRevGrowth({
    firstYearRevGrowth: null,
    threeYearRevGrowth: null,
    inputs: {
      low: "",
      mid: "",
    },
  });
  setProfitMargin({
    firstYearProfitMargin: null,
    threeYearProfitMargin: null,
    inputs: {
      low: "",
      mid: "",
    },
  });
  setFcfMargin({
    firstYearFcfMargin: null,
    threeYearFcfMargin: null,
    inputs: {
      low: "",
      mid: "",
    },
  });
  if (setDesiredAnualReturn) {
    setDesiredAnualReturn({
      inputs: {
        low: "",
        mid: "",
      },
    });
  }
}

export const calculateStockValues = ({
  revGrowth,
  years,
  profitMargin,
  peRatios,
  currentRevenue,
  sharesOutstanding,
  currentStockPrice,
  setFuturePrices,
  financialCurrency,
}) => {
  try {
    // Basic validation
    if (!revGrowth?.inputs?.low || !profitMargin?.inputs?.low || !peRatios?.inputs?.low) {
      console.error("Missing required inputs");
      return;
    }

    // Calculate future revenue
    const lowRevGrowth = parseFloat(revGrowth.inputs.low) / 100;
    const futureRevenueLow = currentRevenue * Math.pow(1 + lowRevGrowth, years);

    const midRevGrowth = parseFloat(revGrowth.inputs.mid) / 100;
    const futureRevenueMid = currentRevenue * Math.pow(1 + midRevGrowth, years);

    // Calculate future earnings using profit margins
    const lowProfitMargin = parseFloat(profitMargin.inputs.low) / 100;
    const midProfitMargin = parseFloat(profitMargin.inputs.mid) / 100;

    const futureProfitLow = futureRevenueLow * lowProfitMargin;
    const futureProfitMid = futureRevenueMid * midProfitMargin;

    // Calculate future stock prices using P/E ratios
    const lowPE = parseFloat(peRatios.inputs.low);
    const midPE = parseFloat(peRatios.inputs.mid);

    const futurePriceLow = (futureProfitLow / sharesOutstanding) * lowPE;
    const futurePriceMid = (futureProfitMid / sharesOutstanding) * midPE;

    setFuturePrices({
      low: {
        futureStockPrice: futurePriceLow.toFixed(2),
        currentPriceReturn: (
          ((futurePriceLow - currentStockPrice) / currentStockPrice) *
          100
        ).toFixed(2),
      },
      mid: {
        futureStockPrice: futurePriceMid.toFixed(2),
        currentPriceReturn: (
          ((futurePriceMid - currentStockPrice) / currentStockPrice) *
          100
        ).toFixed(2),
      },
    });
  } catch (error) {
    console.error("Error in calculateStockValues:", error);
  }
};